import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Search, Download, FileSpreadsheet } from "lucide-react";

const ACTION_LABELS = {
  "user.create": { label: "Creare cont", cls: "bg-emerald-100 text-emerald-700" },
  "user.update": { label: "Modificare cont", cls: "bg-amber-100 text-amber-700" },
  "user.deactivate": { label: "Dezactivare cont", cls: "bg-orange-100 text-orange-700" },
  "user.delete": { label: "Ștergere cont", cls: "bg-red-100 text-red-700" },
  "data.purge_employees": { label: "Ștergere toți angajații", cls: "bg-red-100 text-red-700" },
  "data.purge_timesheets": { label: "Ștergere pontaje", cls: "bg-red-100 text-red-700" },
  "data.purge_timeoff": { label: "Ștergere concedii", cls: "bg-red-100 text-red-700" },
  "data.purge_assignments": { label: "Ștergere alocări", cls: "bg-red-100 text-red-700" },
  "data.import": { label: "Import date (Excel)", cls: "bg-blue-100 text-blue-700" },
  "project.create": { label: "Creare proiect", cls: "bg-emerald-100 text-emerald-700" },
  "project.update": { label: "Modificare proiect", cls: "bg-amber-100 text-amber-700" },
  "project.delete": { label: "Ștergere proiect", cls: "bg-red-100 text-red-700" },
  "project.financials": { label: "Modificare finanțe proiect", cls: "bg-amber-100 text-amber-700" },
  "project.set_manager": { label: "Setare manager proiect", cls: "bg-amber-100 text-amber-700" },
  "assignment.create": { label: "Alocare pe proiect", cls: "bg-emerald-100 text-emerald-700" },
  "assignment.update": { label: "Modificare alocare", cls: "bg-amber-100 text-amber-700" },
  "assignment.delete": { label: "Eliminare alocare", cls: "bg-orange-100 text-orange-700" },
  "report.save": { label: "Salvare raport", cls: "bg-blue-100 text-blue-700" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}, ${hh}:${mi}`;
}

function renderChanges(entry) {
  const pretty = entry.changes_pretty;
  if (pretty && pretty.length) {
    return (
      <div className="space-y-1">
        {pretty.map((c, i) => (
          <div key={i} className="text-[12px] leading-snug">
            <span className="text-ink-muted">{c.label}: </span>
            {"din" in c ? (
              <>
                <span className="text-red-600 line-through">{c.din}</span>
                <span className="mx-1 text-ink-muted">→</span>
                <span className="text-emerald-700 font-medium">{c.in}</span>
              </>
            ) : (
              <span className="text-ink font-medium">{c.value}</span>
            )}
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-ink-muted">Fără detalii suplimentare</span>;
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyAdmins, setOnlyAdmins] = useState(false);
  const [dlBusy, setDlBusy] = useState(null);

  async function exportAudit(format) {
    setDlBusy(format);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/staff/audit/export?format=${format}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit-trail.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Export ${format.toUpperCase()} descărcat`);
    } catch (e) { toast.error(`Export eșuat: ${e.message || e}`); }
    finally { setDlBusy(null); }
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff/audit?limit=500");
        setEntries(data || []);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return entries.filter(e =>
      (!onlyAdmins || e.target_role === "admin") &&
      (!s || [e.actor_name, e.actor_email, e.target_name, e.target_email, e.action]
        .some(x => (x || "").toLowerCase().includes(s)))
    );
  }, [entries, q, onlyAdmins]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Setări</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2 flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-brand" /> Audit
          </h1>
          <p className="text-ink-muted text-sm">Istoricul complet al modificărilor din platformă: cine, când, ce anume și pe cine a afectat — pe înțelesul tuturor.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative w-full sm:w-auto">
            <Search className="h-4 w-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} data-testid="audit-search"
              placeholder="Caută după persoană sau acțiune..."
              className="border border-neutral-300 pl-9 pr-3 py-2 text-sm bg-white w-full sm:w-80" />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-muted cursor-pointer">
            <input type="checkbox" checked={onlyAdmins} onChange={e => setOnlyAdmins(e.target.checked)} data-testid="audit-only-admins" />
            Doar conturi admin
          </label>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-ink-muted">{filtered.length} înregistrări</span>
            <button onClick={() => exportAudit("csv")} disabled={dlBusy} data-testid="audit-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-sm transition-colors disabled:opacity-60">
              {dlBusy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
            </button>
            <button onClick={() => exportAudit("xlsx")} disabled={dlBusy} data-testid="audit-export-xlsx"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-white hover:bg-brand text-sm rounded transition-colors disabled:opacity-60">
              {dlBusy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-neutral-200 rounded-xl text-ink-muted" data-testid="audit-empty">
            Nicio înregistrare de audit.
          </div>
        ) : (
          <>
          <div className="md:hidden" data-testid="audit-cards">
            {filtered.slice(0, 150).map((e) => {
              const a = ACTION_LABELS[e.action] || { label: e.action_label || e.action, cls: "bg-neutral-100 text-ink-muted" };
              const targetLabel = e.target_name || e.target_email;
              return (
                <div key={e.id} className="m-card" data-testid="audit-card">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${a.cls}`}>{a.label}</span>
                    <span className="font-mono text-[11px] text-ink-muted whitespace-nowrap">{fmtDateTime(e.at)}</span>
                  </div>
                  <div className="m-kv"><span className="m-kv-label">Cine</span><span className="m-kv-value">{e.actor_name || e.actor_email || "—"}</span></div>
                  <div className="m-kv"><span className="m-kv-label">Vizat</span><span className="m-kv-value">{targetLabel || <span className="italic text-ink-muted font-normal">Întreaga platformă</span>}</span></div>
                  <div className="mt-2 text-[12.5px] text-ink">{renderChanges(e)}</div>
                </div>
              );
            })}
            {filtered.length > 150 && <div className="text-center text-[12px] text-ink-muted py-3">Se afișează primele 150 — rafinează căutarea sau exportă.</div>}
          </div>
          <div className="bg-white border border-neutral-200 overflow-x-auto hidden md:block" data-testid="audit-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200">Data</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200">Cine</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200">Ce s-a întâmplat</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200">Cont / element vizat</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200">Ce s-a modificat</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const a = ACTION_LABELS[e.action] || { label: e.action_label || e.action, cls: "bg-neutral-100 text-ink-muted" };
                  const targetLabel = e.target_name || e.target_email;
                  return (
                    <tr key={e.id} className="hover:bg-ink-soft/40 align-top" data-testid="audit-row">
                      <td className="px-3 py-2.5 border-b border-neutral-100 font-mono text-[12px] whitespace-nowrap">{fmtDateTime(e.at)}</td>
                      <td className="px-3 py-2.5 border-b border-neutral-100">
                        <div className="font-medium text-ink">{e.actor_name || "—"}</div>
                        <div className="text-[11px] text-ink-muted">{e.actor_email}</div>
                      </td>
                      <td className="px-3 py-2.5 border-b border-neutral-100">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${a.cls}`}>{a.label}</span>
                      </td>
                      <td className="px-3 py-2.5 border-b border-neutral-100">
                        {targetLabel ? (
                          <>
                            <div className="font-medium text-ink flex items-center gap-1.5">
                              {targetLabel}
                              {e.target_role === "admin" && <span className="text-[9px] font-mono uppercase bg-brand/10 text-brand px-1 py-0.5 rounded">admin</span>}
                            </div>
                            {e.target_email && <div className="text-[11px] text-ink-muted">{e.target_email}</div>}
                          </>
                        ) : (
                          <span className="text-ink-muted italic text-[12px]">Întreaga platformă</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 border-b border-neutral-100 max-w-[460px]">{renderChanges(e)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
